import type { PriceMap, TCaption } from '@/types/common.types';
import { createContext, useContext } from 'react';
import * as Sentry from '@sentry/react';
import { resolutions } from '@/constants';
import type { Location } from 'react-router-dom';
// import ROUTES from '@/routes/path';

export const ToastContext = createContext<(message: string) => void>(() => {});
export const useToast = () => useContext(ToastContext);

export const LayoutVisibilityContext = createContext<{
  showFooter: boolean;
  setShowFooter: (show: boolean) => void;
  showHeader: boolean;
  setShowHeader: (show: boolean) => void;
  isMobile: boolean;
  isLandscape: boolean;
  hasIntercom: boolean;
  setHasIntercom: (show: boolean) => void;
  _isIOS: boolean;
  setAppHeight: () => void;
  setShowFooterLifeX: (show: boolean) => void;
  showFooterLifeX: boolean;
  setShowMenu: (show: boolean) => void;
  showMenu: boolean;
}>({
  showFooter: true,
  isMobile: false,
  setShowFooter: () => {},
  showHeader: true,
  setShowHeader: () => {},
  isLandscape: false,
  hasIntercom: false,
  setHasIntercom: () => {},
  _isIOS: false,
  setAppHeight: () => {},
  setShowFooterLifeX: () => {},
  showFooterLifeX: false,
  setShowMenu: () => {},
  showMenu: false,
});
export const useLayoutVisibility = () => useContext(LayoutVisibilityContext);

export const decodeBase64 = (value: string | null): string => {
  try {
    return value ? atob(value) : '';
  } catch {
    return '';
  }
};

export const cn = (...classes: string[]) => {
  return classes.filter(Boolean).join(' ');
};

export const createCaptionsTrack = (captions: TCaption[]): HTMLTrackElement => {
  let vtt = 'WEBVTT\n\n';

  captions.forEach((caption, index) => {
    vtt += `${index + 1}\n`;
    vtt += `${caption.start} --> ${caption.end}\n`;
    vtt += `${caption.text}\n\n`;
  });

  const blob = new Blob([vtt], { type: 'text/vtt' });
  const url = URL.createObjectURL(blob);

  const track: HTMLTrackElement = document.createElement('track');
  track.src = url;
  track.kind = 'subtitles';
  track.srclang = 'en';
  track.label = 'English';
  track.default = true;

  return track;
};

export const isTouchDevice = () =>
  typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

export const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

export const detectMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isUserAgentMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(userAgent);
  const isSmallScreen = window.innerWidth <= 768; // Adjust if needed
  return isUserAgentMobile || isSmallScreen;
};

export const requestLandscape = (location: Location<any>) => {
  console.log(location);
  // const orientation = (screen.orientation || (screen as any).mozOrientation || (screen as any).msOrientation) as any;
  // if (location.pathname.includes(ROUTES.twinInterview)) {
  //   if (document.fullscreenElement) {
  //     document
  //       .exitFullscreen()
  //       .then(() => {
  //         (screen.orientation as any)?.unlock?.();
  //         (screen.orientation as any)?.lock?.('portrait').catch((e: any) => {
  //           consoleError('Exit FullScreenError (Lock portrait)', e);
  //         });
  //       })
  //       .catch(e => {
  //         consoleError('Exit FullScreenError', e);
  //       });
  //   }
  // } else {
  //   if (orientation && orientation.lock) {
  //     const elem = document.documentElement;
  //     if (elem.requestFullscreen) {
  //       elem
  //         .requestFullscreen()
  //         .then(() => {
  //           // Use 'landscape-primary' to rotate to the right side (primary landscape)
  //           orientation.lock('landscape-secondary').catch((e: any) => {
  //             consoleError('FullScreenError (lock landscape-primary)', e);
  //           });
  //         })
  //         .catch(e => {
  //           consoleError('FullScreenError', e);
  //         });
  //     }
  //   }
  // }
};

export const consoleError = (context: string = '', error: unknown) => {
  console.error(context, error);

  Sentry.captureEvent({
    message: `${context}`,
    level: 'error',
    extra: { error },
  });
  // Sentry.captureReactException(error, { componentStack: context });
};

export const consoleInfo = (context: string = '', message: unknown, extra: Record<string, any> = {}) => {
  console.info(context, message);

  Sentry.captureEvent({
    message: `${context} ${String(message)}`,
    level: 'info',
    extra,
  });
};

export const findBestResolution = async (deviceId: string) => {
  for (const resolution of resolutions) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { exact: resolution.width },
          height: { exact: resolution.height },
        },
      });
      // Stop all tracks to release the camera
      stream.getTracks().forEach(track => track.stop());
      return resolution;
    } catch (err) {
      console.log(`Resolution ${resolution.width}x${resolution.height} not supported`, err);
    }
  }
  return null;
};

export const replaceLocalizedPrices = (
  options: { label: string; value: string }[],
  priceMap: PriceMap
): { label: string; value: string }[] => {
  return options.map(opt => {
    const updatedLabel = opt.label.replace(/<\$([\w_]+)>/g, (_, key) => {
      return priceMap[key] ?? 'N/A';
    });
    return { ...opt, label: updatedLabel };
  });
};

export async function runWithLiveProgress<T>(
  task: () => Promise<T>,
  durationEstimate: number,
  progressStart: number,
  progressEnd: number,
  updateProgress: (progress: number) => void
): Promise<T> {
  const startTime = Date.now();

  const timer = setInterval(() => {
    const elapsed = Date.now() - startTime;
    const percent = Math.min(1, elapsed / durationEstimate);
    updateProgress(progressStart + percent * (progressEnd - progressStart));
  }, 100);

  try {
    const result = await task();
    updateProgress(progressEnd);
    return result;
  } finally {
    clearInterval(timer);
  }
}

export const safePlay = async (media: HTMLMediaElement) => {
  try {
    await media.play();
  } catch (err: any) {
    consoleInfo('Media', 'Failed to autoplay media element', { error: err });
  }
};

export const safeCleanupAudio = (audio: HTMLAudioElement) => {
  try {
    audio.pause();
    audio.currentTime = 0;
    audio.src = '';
    audio.load();
  } catch (err: any) {
    consoleInfo('Audio', 'Failed to cleanup audio element', { error: err });
  }
};

export function formatDate(dateString: string): string {
  const date = new Date(dateString);

  // Format using Intl.DateTimeFormat
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Get the epoch time (in seconds) for the start of the day in UTC.
 *
 * @param date Optional Date (default: now)
 * @returns Epoch time in seconds (UTC 00:00:00)
 */
export function getEpochStartOfDayUTC(date: Date = new Date()): number {
  const startOfDayUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));

  return Math.floor(startOfDayUTC.getTime() / 1000);
}
